import React, { useState } from 'react';
import { toast } from 'sonner';

/**
 * Mindbody Push-Status Badge mit Retry-Button (Iter 329d, erweitert 08/2026).
 *
 * Zeigt pro Termin den Sync-Status mit Mindbody:
 *   🟢 Synced      → Push erfolgreich (mindbody_pushed_id vorhanden)
 *   🟡 Pushing     → Push läuft gerade (pending)
 *   🔴 Failed      → Push fehlgeschlagen — Retry-Button
 *   ⚪ Nicht synct → Push-relevanter Service, aber (noch) nie gepusht
 *                    (nur für ZUKÜNFTIGE Termine, mit Retry-Button)
 *   ⚪ Skipped     → Push übersprungen (z.B. kein Staff-Mapping) — Tooltip
 *
 * `pushRelevant` = true wenn der Service push_to_mindbody hat.
 */

const SKIP_REASONS_DE = {
  no_reverse_staff_mapping: 'Kein Mindbody-Staff-Mapping für diesen Trainer',
  service_not_marked_for_push: 'Service ist nicht für Mindbody-Push markiert',
  push_disabled_or_missing_config: 'Mindbody-Push ist deaktiviert',
  no_appointment_id: 'Termin ohne ID',
};

export default function MindbodyPushBadge({
  appointment,
  onRetryDone,
  compact = false,
  pushRelevant = false,
}) {
  const [busy, setBusy] = useState(false);
  const status = appointment?.mindbody_push_status;
  const isPushed = !!(
    appointment?.mindbody_pushed_id ||
    appointment?.mindbody_pushed_unavailability_id
  );
  const isMirror =
    appointment?.source === 'mindbody' || appointment?.is_mindbody_mirror;

  let isFuture = false;
  try {
    isFuture = new Date(appointment?.start_time).getTime() > Date.now();
  } catch {
    /* noop */
  }

  // Effektiver Zustand
  let state = null;
  if (status === 'success' || (isPushed && !status)) state = 'success';
  else if (status === 'pending') state = 'pending';
  else if (status === 'failed') state = 'failed';
  else if (status === 'skipped') state = 'skipped';
  else if (pushRelevant && !isMirror && isFuture) state = 'notsynced';

  if (!state) return null;

  const dotSize = compact ? 'w-2.5 h-2.5' : 'w-3 h-3';
  const wrapperBase = 'absolute top-0 left-1/2 -translate-x-1/2 z-20 flex items-center gap-1';

  let bgClass;
  let title;
  let showRetry = false;

  if (state === 'success') {
    bgClass = 'bg-emerald-500';
    title = `Mindbody Sync OK${appointment.mindbody_pushed_at ? ` · ${new Date(appointment.mindbody_pushed_at).toLocaleString('de-CH')}` : ''}`;
  } else if (state === 'pending') {
    bgClass = 'bg-amber-400 animate-pulse';
    title = 'Mindbody-Push läuft …';
  } else if (state === 'failed') {
    bgClass = 'bg-rose-600';
    title = `Mindbody-Push fehlgeschlagen${appointment.mindbody_push_error ? `: ${appointment.mindbody_push_error}` : ''}`;
    showRetry = true;
  } else if (state === 'skipped') {
    bgClass = 'bg-gray-400';
    const reason = SKIP_REASONS_DE[appointment.mindbody_push_skip_reason]
      || appointment.mindbody_push_skip_reason
      || 'Push übersprungen';
    title = `Nicht zu Mindbody gesynct — ${reason}`;
    showRetry = true;
  } else {
    // notsynced
    bgClass = 'bg-gray-400';
    title = 'Noch nicht zu Mindbody gesynct — wird automatisch nachgeholt (oder ↻ für sofort)';
    showRetry = true;
  }

  const handleRetry = async (e) => {
    e?.stopPropagation();
    if (busy || !showRetry) return;
    try {
      setBusy(true);
      const tok = localStorage.getItem('token') || '';
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/mindbody/appointments/${appointment.id}/retry-push`;
      const res = await fetch(url, {
        method: 'POST',
        headers: { Authorization: `Bearer ${tok}` },
      });
      const data = await res.json();
      if (res.ok && data?.ok) {
        toast.success('Mindbody-Push erfolgreich');
      } else if (data?.result?.skipped) {
        const r = SKIP_REASONS_DE[data.result.reason] || data.result.reason || 'unbekannt';
        toast(`Push übersprungen: ${r}`);
      } else {
        toast.error(`Mindbody-Push fehlgeschlagen: ${data?.result?.error || 'unbekannt'}`);
      }
      onRetryDone?.(data?.appointment || null);
    } catch (err) {
      toast.error(`Retry-Fehler: ${err?.message || err}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={wrapperBase} title={title} data-testid={`mb-push-badge-${appointment.id}`}>
      <span
        className={`${dotSize} rounded-full ${bgClass} shadow border border-white`}
        data-testid={`mb-push-status-${appointment.id}-${state}`}
      />
      <span className="text-[8px] font-bold text-black/80 leading-none">MB</span>
      {showRetry && (
        <button
          type="button"
          onClick={handleRetry}
          disabled={busy}
          className="ml-0.5 text-[8px] font-bold text-gray-700 bg-white/90 rounded px-1 leading-none border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
          title="Mindbody-Push jetzt versuchen"
          data-testid={`mb-push-retry-${appointment.id}`}
        >
          {busy ? '…' : '↻'}
        </button>
      )}
    </div>
  );
}
